/**
 Copyright (c) 2015, 2018, Oracle and/or its affiliates.
 The Universal Permissive License (UPL), Version 1.0
 */

'use strict';

define('vb/components/mobile-navigation-bar-bottom/viewModel',[
  'ojs/ojarraydataprovider',
  'ojs/ojthemeutils',
  'ojs/ojbutton',
  'ojs/ojnavigationlist',
], (ArrayDataProvider, ThemeUtils) => {
  /**
   * @typedef {Object} NavigationItem
   * @property {string|null} name
   * @property {string|null} view
   * @property {string|null} action
   * @property {boolean} takeFocus
   * @property {string|null} iconClass
   */

  class NavigationBarBottomComponentModel {
    constructor(context) {
      this.composite = context.element;
      this.configure();
    }

    /**
     * Configures the composite component
     */
    configure() {
      // bottom bar is bigger in android so set a marker style for android theme
      const themeTargetPlatform = ThemeUtils.getThemeTargetPlatform();
      if (themeTargetPlatform === 'android') {
        this.composite.classList.add('oj-ext-android-theme');
      } else if (themeTargetPlatform === 'ios') {
        this.composite.classList.add('oj-ext-ios-theme');
      } else if (ThemeUtils.getThemeName() === 'redwood') {
        this.composite.classList.add('oj-ext-redwood-theme');
      }
      /**
       *  New, dynamic NavBar code
       *
       *  This code fetches the oj-ext-mobile-navigation-bar-bottom node
       *  and then scans that tag for any sub-   oj-ext-mobile-menuitem  elements.
       *  For each of those menu items, it creates an array entry object and adds it to the navBar array.
       */
      /**
       * @param {HTMLElement} node
       * @returns {NavigationItem}
       */
      const parseNavigationItemFromHTML = (node) => {
        const item = {};

        item.name = node.getAttribute('name');
        item.view = node.getAttribute('view');
        item.action = node.getAttribute('action');
        item.takeFocus = !(node.getAttribute('takeFocus') === 'false');
        item.iconClass = node.getAttribute('iconclass');

        return item;
      };

      /** @type {Array<NavigationItem>} */
      const navData = [];
      const menuItems = this.composite.getElementsByTagName('oj-ext-mobile-navigation-item');

      // menuItems is not an array but htmlElement Collection
      for (let i = 0; i < menuItems.length; i += 1) {
        navData.push(parseNavigationItemFromHTML(menuItems[i]));
      }

      /** @type {Array<NavigationItem>} */
      this.navData = navData;
      this.navDataSource = new ArrayDataProvider(navData, { idAttribute: 'action' });
    }

    /**
     * Private method to raise navigation action
     * @param sourceEvent {Event}
     * @param navItem {NavigationItem}
     * @private
     */
    _raiseNavigationAction(sourceEvent, navItem) {
      const eventParams = {
        detail: {
          value: navItem.action,
        },
      };
      this.composite.dispatchEvent(new CustomEvent('navigationAction', eventParams));
    }

    /**
     *   This defines the CCA reaction to the selection of a navBar item.  It should check:
     *   1) The action attribute and if defined raise a shellAction with the selected navItem's
     *   configured value.
     *   2) The view attribute and if defined alter the selection property to match.
     */
    navBarChangeHandler(event) {
      // ignore selection triggered through external property changes. i.e only handle direct user interaction
      if (event.detail.updatedFrom !== 'external') {
        const navItem = this._findNavItem(event.detail.value);

        if (navItem.action) {
          this._raiseNavigationAction(event, navItem);
        }
      }
    }

    /**
     * Hook into the ojBeforeSelect event to control whether the nav list item retains highlighting,
     * based on the takeFocus attribute.
     */
    beforeSelectHandler(event) {
      // in the beforeSelect event,event.detail.key holds the selected id
      const navItem = this._findNavItem(event.detail.key);

      if (!navItem.takeFocus) {
        event.preventDefault();
      }
    }

    /**
     * returns the navigation item in the internal list that matches the target action
     * @param targetAction
     * @returns {NavigationItem}
     * @private
     */
    _findNavItem(targetAction) {
      return /** @type {NavigationItem} */ (this.navData.find((navItem) => navItem.action === targetAction));
    }
  }

  return NavigationBarBottomComponentModel;
});

